import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { 
  Menu,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Import SVG icons
import dashboardIcon from "@/assets/dashboard/sidebar/dashboard.svg";
import referralsIcon from "@/assets/dashboard/sidebar/referrals.svg";
import jobsIcon from "@/assets/dashboard/sidebar/jobs.svg";
import rewardsIcon from "@/assets/dashboard/sidebar/rewards.svg";
import clientReferralIcon from "@/assets/dashboard/sidebar/client_referral.svg";
import logoutIcon from "@/assets/dashboard/sidebar/logout.svg";
import logo from "/logo.png";

interface SidebarItem {
  icon: string;
  label: string;
  href: string;
  active?: boolean;
}

export function Sidebar() {
  const [location] = useLocation();
  const { logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const sidebarItems: SidebarItem[] = [
    {
      icon: dashboardIcon,
      label: "Dashboard",
      href: "/dashboard",
      active: location === "/dashboard"
    },
    {
      icon: jobsIcon,
      label: "Available Jobs",
      href: "/jobs",
      active: location === "/jobs" || location.startsWith("/jobs/")
    },
    {
      icon: referralsIcon,
      label: "My Referrals",
      href: "/referrals",
      active: location === "/referrals"
    },
    {
      icon: clientReferralIcon,
      label: "Client Referral",
      href: "/client-referral",
      active: location === "/client-referral"
    },
    {
      icon: rewardsIcon,
      label: "Rewards",
      href: "/rewards",
      active: location === "/rewards"
    }
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="bg-white border-gray-200"
        >
          {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 
        w-64 lg:w-56 bg-white border-r border-gray-200 h-screen flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center">
            <img 
              src={logo} 
              alt="AirTalent Logo" 
              className="h-7 w-auto object-contain"
            />
          </div>
        </div>

        {/* Menu Label */}
        <div className="p-4">
          <p className="text-gray-400 uppercase font-semibold" style={{
            fontFamily: 'Manrope',
            fontWeight: 600,
            fontSize: '10px',
            lineHeight: '140%',
            letterSpacing: '0%'
          }}>
            MENU
          </p>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {sidebarItems.map((item) => {
            return (
              <Link key={item.href} href={item.href}>
                <div
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors space-grotesk font-medium cursor-pointer ${
                    item.active
                      ? "border-b border-orange-500"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <img 
                    src={item.icon} 
                    alt={item.label}
                    className={`h-5 w-5 transition-colors ${
                      item.active 
                        ? "brightness-0 saturate-100" 
                        : "opacity-60 hover:opacity-80"
                    }`}
                    style={item.active ? { 
                      filter: "brightness(0) saturate(100%) invert(45%) sepia(69%) saturate(959%) hue-rotate(346deg) brightness(97%) contrast(94%)" 
                    } : {
                      filter: "brightness(0) saturate(100%) invert(53%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(53%) contrast(88%)"
                    }}
                  />
                  <span style={{
                    fontFamily: 'Manrope',
                    fontWeight: item.active ? 600 : 400,
                    fontSize: '14px',
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    color: item.active ? '#2D2D2D' : '#888888'
                  }}>{item.label}</span>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-gray-200 space-y-1">
          <Button
            variant="ghost"
            onClick={() => {
              handleLogout();
              setIsMobileMenuOpen(false);
            }}
            className="w-full justify-start gap-3 px-3 py-3 text-gray-600 hover:bg-gray-50 space-grotesk font-medium"
            data-testid="button-logout"
          >
            <img 
              src={logoutIcon} 
              alt="Logout"
              className="h-5 w-5 opacity-60 hover:opacity-80 transition-opacity"
            />
            <span style={{
              fontFamily: 'Manrope',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '140%',
              letterSpacing: '0%',
              color: '#888888'
            }}>Logout</span>
          </Button>
        </div>
      </div>
    </>
  );
}